import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Award,
  Send,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { useCurrentUser } from '../../hooks/useAuth';
import { Button } from '../../components/ui/Button';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { api, ApiError } from '../../lib/api';

interface SendResult {
  attempted: number;
  sent: number;
  skipped: number;
  errors: Array<{ person_id: string; message: string }>;
}

const ALLOWED = ['admin', 'coordinator'];

/**
 * End-of-day completion certificates (CLAUDE.md 6.3).
 */
export function CertificatesAdmin() {
  const { data: user, isLoading } = useCurrentUser();
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<SendResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (isLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  if (!user.roles.some((r) => ALLOWED.includes(r))) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Restricted to admin / coordinator.
      </main>
    );
  }

  async function send() {
    setSending(true);
    setError(null);
    setResult(null);
    try {
      const r = await api<SendResult>('/api/admin/certificates/send-all', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setResult(r);
      toast.success(
        `Sent ${r.sent} of ${r.attempted}${r.skipped > 0 ? ` (${r.skipped} skipped)` : ''}`,
      );
      setConfirmOpen(false);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'send failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
            <Award className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-accent">
              Admin
            </div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Completion certificates
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              End-of-day. One PDF per attendee, sent as an email attachment.
              The system also auto-sends a certificate the moment an attendee
              finishes their final station, so this bulk button is mostly a
              safety net. Already-sent attendees are skipped automatically.
              To force a re-send to one person, open their record and use
              the "Re-send certificate" button there.
            </p>
          </div>
        </header>

        <section className="mt-6 rounded-lg border border-line/10 bg-surface p-5 shadow-sm sm:p-6">
          <Button
            variant="primary"
            size="md"
            iconLeft={<Send className="h-4 w-4" />}
            loading={sending}
            onClick={() => setConfirmOpen(true)}
          >
            Send certificates to arrived attendees
          </Button>

          <ConfirmModal
            open={confirmOpen}
            tone="warning"
            icon={Send}
            title="Send certificates to every arrived attendee?"
            message={
              <>
                Each attendee who arrived today gets the PDF as an email
                attachment. Already-sent attendees are skipped automatically.
              </>
            }
            confirmLabel="Send now"
            cancelLabel="Cancel"
            onConfirm={send}
            onClose={() => setConfirmOpen(false)}
          />

          {error ? (
            <div className="mt-4 flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          {result ? (
            <div className="mt-4 rounded-md border border-line/10 bg-surface-2 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span>
                  Attempted <strong className="tabular-nums">{result.attempted}</strong>,
                  sent <strong className="tabular-nums">{result.sent}</strong>
                  {result.skipped > 0 ? (
                    <>
                      , skipped <strong className="tabular-nums">{result.skipped}</strong>{' '}
                      (already sent)
                    </>
                  ) : null}
                  .
                </span>
              </div>
              {result.errors.length > 0 ? (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs text-danger-soft-fg">
                    {result.errors.length} error
                    {result.errors.length === 1 ? '' : 's'}
                  </summary>
                  <ul className="mt-2 space-y-1 text-xs text-danger-soft-fg">
                    {result.errors.map((e) => (
                      <li key={e.person_id} className="font-mono">
                        <code className="text-fg-muted">{e.person_id.slice(0, 8)}</code>
                        : {e.message}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </div>
          ) : null}
        </section>
      </div>
    </main>
  );
}
